import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'

import styles from './TipEntryModal.module.scss'
import { ButtonBase } from '@material-ui/core'
import { isE700Device } from '../VNAndroidSDK/Utils'

const formatValue = (value) => (parseInt(value, 10) / 100 || 0).toFixed(2)

const TipEntryModal = ({
  title,
  initialValue,
  saveButtonText,
  onSubmit,
  onClose,
  isKiosk,
  noCloseOnSubmit,
  opaque = false,
  preselected = false,
}) => {
  const inputRef = useRef(null)
  const [value, setValue] = useState(formatValue(initialValue) || '0.00')

  useEffect(() => {
    setValue(formatValue(initialValue))
  }, [initialValue])

  const handleUpdateValue = () => {
    onSubmit(Math.round(value * 100))
    if (noCloseOnSubmit) return
    onClose()
  }

  const handleEnter = (event) => {
    if (event.key === 'Enter' || event.keyCode === 13) {
      handleUpdateValue()
      return
    }
  }

  const handleInput = (event) => {
    // If Enter was pressed, submit the entered value
    if (event.key === 'Enter' || event.keyCode === 13) {
      handleUpdateValue()
      return
    }

    let input = event.target.value?.replace(/\D/g, '')
    let formattedValue = (parseInt(input, 10) / 100).toFixed(2)

    // Save the current cursor position before updating the value
    setValue(formattedValue)

    // Move the cursor to the correct position after the input value is updated
    const cursorPosition = event.target.selectionStart
    const valueLength = formattedValue.length + 1
    if (cursorPosition <= 4) {
      setTimeout(() => {
        event.target.setSelectionRange(valueLength, valueLength)
      }, 0)
    }
  }

  useEffect(() => {
    if (!preselected) return

    inputRef?.current?.select()
  }, [preselected])

  return (
    <div className={isKiosk ? styles.kioskTipEntryModal : styles.tipEntryModal}>
      <div className={cn([styles.overlay, opaque && styles.opaque])}>
        <div className={styles.content}>
          <div className={styles.tipText}>{title}</div>
          <div className={styles.inputContainer}>
            <input
              type="tel"
              placeholder="$0.00"
              value={`$${value}`}
              onChange={handleInput}
              onKeyDown={handleEnter}
              pattern="\d*"
              autoFocus
              ref={inputRef}
            />
          </div>
          <div className={styles.buttonContainer}>
            <ButtonBase disableRipple={isE700Device()} onClick={onClose} className={styles.cancelButton}>
              Cancel
            </ButtonBase>
            <div className={styles.partition} />
            <ButtonBase disableRipple={isE700Device()} onClick={handleUpdateValue} className={styles.doneButton}>
              {saveButtonText}
            </ButtonBase>
          </div>
        </div>
      </div>
    </div>
  )
}

TipEntryModal.defaultProps = {
  title: '',
  errorMessage: '',
  initialValue: 0,
  saveButtonText: 'SAVE',
  validator: () => {},
  onSubmit: () => {},
  onClose: () => {},
}

TipEntryModal.propTypes = {
  title: PropTypes.string,
  errorMessage: PropTypes.string,
  initialValue: PropTypes.number,
  saveButtonText: PropTypes.string,
  validator: PropTypes.func,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
}

export default TipEntryModal
